import { useNavigate } from "@tanstack/react-router"
import { useEffect } from "react"

import CloseIconV2 from "@/assets/img/modal/close_icon_v2.svg"
import EventImage from "@/assets/img/modal/popover_event_image.webp"
import Dot from "@/assets/svg/advisors/Dot.svg?react"
import { advisorsList } from "@/features/advisors"
import { useChat } from "@/features/chat"
import { WebappRoutes } from "@/shared/lib/webapp-routes"

import { getActiveAstrologyEvent } from "../config/astrology-events"
import type { AppModalComponentProps } from "../config/modal-registry"

/**
 * Astrology event promo sheet (Figma: Popovers_Event, 3114:51575).
 * Only shown when `getActiveAstrologyEvent()` returns an active event.
 */
export function EventModal({ onClose }: AppModalComponentProps) {
  const navigate = useNavigate()
  const { setSelectedAdvisor } = useChat()
  const event = getActiveAstrologyEvent()

  useEffect(() => {
    if (!event) onClose()
  }, [event, onClose])

  if (!event) return null

  const advisor =
    advisorsList.find((a) => a.name === event.advisorName) ?? advisorsList[0]
  const firstName = advisor.name.split(" ")[0] ?? advisor.name

  const handleStartChat = () => {
    setSelectedAdvisor(advisor)
    navigate({ to: WebappRoutes.READINGS })
    onClose()
  }

  return (
    <div
      data-slot="event-modal"
      className="fixed inset-0 z-[1200] mx-auto flex min-h-[100dvh] w-full max-w-app flex-col justify-end bg-dark-base/80 backdrop-blur-[10px]"
    >
      <div className="relative flex w-full flex-col overflow-hidden rounded-t-3xl bg-dark-surface p-4">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex size-6 items-center justify-center"
        >
          <img src={CloseIconV2} alt="" className="size-6" />
        </button>

        <div className="flex w-full flex-col items-start gap-4 pt-2">
          <h2 className="w-full text-center font-display text-2xl font-medium text-purple-light">
            The Stars Are Shifting
          </h2>

          <div className="flex w-full flex-col overflow-hidden rounded-3xl bg-dark-base shadow-[0_0_16px_rgba(255,83,127,0.2),0_0_32px_#9565fd]">
            <img
              src={EventImage}
              alt=""
              className="pointer-events-none mb-[-56px] h-[220px] w-full object-cover"
            />

            <div className="relative z-[1] flex w-full flex-col gap-3 px-4 pb-4">
              <div className="flex w-full flex-col gap-2">
                <div className="flex flex-col">
                  <p className="font-body text-sm text-purple-light">{event.date}</p>
                  <h3 className="font-display text-xl font-medium text-white">{event.title}</h3>
                </div>
                <p className="font-body text-sm text-purple-light/70">{event.description}</p>
              </div>

              <div className="flex h-[73px] w-full items-end gap-1">
                <div className="flex shrink-0 flex-col items-center gap-0.5">
                  <div className="relative size-11 overflow-hidden rounded-full">
                    <img
                      src={advisor.photo}
                      alt={advisor.name}
                      className="size-full object-cover object-top"
                    />
                  </div>
                  <div className="flex h-4 w-[60px] items-center justify-center gap-1 rounded-[10px] bg-purple-light/10 py-0.5">
                    <Dot className="size-1 text-green" />
                    <span className="font-body text-[10px] font-medium text-purple-light">
                      {firstName}
                    </span>
                  </div>
                </div>

                <div className="relative flex min-w-0 flex-1 items-center rounded-[20px] bg-purple-light/10 px-4 py-2">
                  <p className="font-body text-sm text-purple-light">{event.advisorMessage}</p>
                </div>
              </div>
            </div>
          </div>

          <p className="w-full text-center font-body text-base text-purple-light/70">
            Ask an astrologer how this transit may affect your next steps
          </p>

          <button
            type="button"
            onClick={handleStartChat}
            className="flex h-14 w-full shrink-0 items-center justify-center rounded-[80px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
          >
            Start Chat
          </button>
        </div>
      </div>
    </div>
  )
}
